import React, { useEffect, useMemo, useRef, useState } from "react";
import { LinkIcon, Upload, X } from "lucide-react";
import { createPortal } from "react-dom";
import { TailwindDateField } from "../../../../components/forms/TailwindDateField";
import type { FacultyMember, FacultyProfileDraft } from "../../types/faculty";
import { FACULTY_STATUS_LABELS } from "../../types/faculty";

interface AddFacultyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (faculty: FacultyProfileDraft) => Promise<void> | void;
  facultyInviteLink?: string;
  initialData?: FacultyMember;
}

const defaultEmergencyContact = {
  name: "",
  relationship: "",
  phone: "",
  alternatePhone: "",
};

const getInitialFormData = (member?: FacultyMember): FacultyProfileDraft => {
  if (!member) {
    return {
      firstName: "",
      lastName: "",
      gender: "Female",
      workEmail: "",
      cellPhone: "",
      employeeId: "EMP-NEW",
      hireDate: new Date().toISOString().split("T")[0],
      position: "Teacher",
      employmentType: "Full-time",
      status: "active",
      divisions: [],
      emergencyContact: { ...defaultEmergencyContact },
      profilePhotoRemoved: false,
    };
  }

  return {
    ...member,
    emergencyContact: {
      ...defaultEmergencyContact,
      ...(member.emergencyContact ?? {}),
    },
    profilePhotoRemoved: false,
  };
};

const fieldClassName = (hasError: boolean) =>
  `w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary ${
    hasError ? "border-strawberry" : "border-border"
  }`;

const copyTextToClipboard = async (value: string): Promise<void> => {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  if (typeof document === "undefined") {
    throw new Error("Clipboard is unavailable.");
  }
  const input = document.createElement("textarea");
  input.value = value;
  input.setAttribute("readonly", "");
  input.style.position = "absolute";
  input.style.left = "-9999px";
  document.body.appendChild(input);
  input.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(input);
  if (!copied) {
    throw new Error("Clipboard copy failed.");
  }
};

export function AddFacultyModal({
  isOpen,
  onClose,
  onSave,
  facultyInviteLink,
  initialData,
}: AddFacultyModalProps) {
  const [formData, setFormData] = useState<FacultyProfileDraft>(
    getInitialFormData(initialData),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [inviteCopyStatus, setInviteCopyStatus] = useState<
    "idle" | "copied" | "failed"
  >("idle");
  const inviteCopyResetRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      if (inviteCopyResetRef.current !== null) {
        window.clearTimeout(inviteCopyResetRef.current);
        inviteCopyResetRef.current = null;
      }
      setInviteCopyStatus("idle");
      return;
    }
    setFormData(getInitialFormData(initialData));
    setErrors({});
    setInviteCopyStatus("idle");
  }, [initialData, isOpen]);

  const profilePreview = useMemo(() => {
    if (formData.profilePhotoFile) {
      return URL.createObjectURL(formData.profilePhotoFile);
    }
    return formData.profilePhoto || "";
  }, [formData.profilePhoto, formData.profilePhotoFile]);

  useEffect(() => {
    return () => {
      if (profilePreview.startsWith("blob:")) {
        URL.revokeObjectURL(profilePreview);
      }
    };
  }, [profilePreview]);

  useEffect(() => {
    return () => {
      if (inviteCopyResetRef.current !== null) {
        window.clearTimeout(inviteCopyResetRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  const isEditMode = Boolean(initialData);
  const modalTestId = isEditMode ? "edit-faculty-modal" : "add-faculty-modal";

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!formData.firstName?.trim())
      nextErrors.firstName = "First name is required";
    if (!formData.lastName?.trim())
      nextErrors.lastName = "Last name is required";
    if (!formData.workEmail?.trim())
      nextErrors.workEmail = "Work email is required";
    if (!formData.cellPhone?.trim())
      nextErrors.cellPhone = "Cell phone is required";
    if (!formData.hireDate?.trim())
      nextErrors.hireDate = "Hire date is required";
    if (!formData.emergencyContact?.name?.trim())
      nextErrors.emergencyName = "Emergency contact name is required";
    if (!formData.emergencyContact?.relationship?.trim())
      nextErrors.emergencyRelationship = "Relationship is required";
    if (!formData.emergencyContact?.phone?.trim())
      nextErrors.emergencyPhone = "Emergency contact phone is required";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!validate()) return;
    await onSave(formData);
  };

  const handleCopyInviteLink = async () => {
    if (!facultyInviteLink) return;
    try {
      await copyTextToClipboard(facultyInviteLink);
      setInviteCopyStatus("copied");
    } catch {
      setInviteCopyStatus("failed");
    }

    if (inviteCopyResetRef.current !== null) {
      window.clearTimeout(inviteCopyResetRef.current);
    }
    inviteCopyResetRef.current = window.setTimeout(() => {
      setInviteCopyStatus("idle");
      inviteCopyResetRef.current = null;
    }, 2200);
  };

  const handleEmergencyChange = (
    field: "name" | "relationship" | "phone" | "alternatePhone",
    value: string,
  ) => {
    setFormData((current) => ({
      ...current,
      emergencyContact: {
        ...(current.emergencyContact ?? defaultEmergencyContact),
        [field]: value,
      },
    }));
  };

  const ErrorText = ({ message }: { message?: string }) =>
    message ? <p className="mt-1 text-xs text-strawberry">{message}</p> : null;

  return createPortal(
    <div className="fixed inset-0 z-[120] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pb-20 pt-4 text-center sm:p-0">
        <div
          data-passive-click-surface="true"
          className="fixed inset-0 z-[110] bg-iron-grey/50 backdrop-blur-sm"
          aria-hidden="true"
          onClick={onClose}
        />

        <div
          data-testid={modalTestId}
          className="relative z-[120] inline-block w-full max-w-3xl overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle"
        >
          <div className="border-b border-border bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="mb-5 flex items-center justify-between border-b border-border pb-4">
              <h3
                data-testid="modal-title"
                className="font-heading text-xl font-semibold leading-6 text-iron-grey"
              >
                {isEditMode ? "Edit faculty member" : "Add new faculty member"}
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="text-text-secondary transition-colors hover:text-iron-grey"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="max-h-[60vh] space-y-8 overflow-y-auto pr-2"
            >
              <section>
                <h4 className="mb-4 font-title text-sm font-semibold uppercase tracking-wider text-iron-grey">
                  Basic information
                </h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      First name <span className="text-strawberry">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.firstName || ""}
                      onChange={(event) =>
                        setFormData((current) => ({
                          ...current,
                          firstName: event.target.value,
                        }))
                      }
                      className={fieldClassName(Boolean(errors.firstName))}
                    />
                    <ErrorText message={errors.firstName} />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Last name <span className="text-strawberry">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.lastName || ""}
                      onChange={(event) =>
                        setFormData((current) => ({
                          ...current,
                          lastName: event.target.value,
                        }))
                      }
                      className={fieldClassName(Boolean(errors.lastName))}
                    />
                    <ErrorText message={errors.lastName} />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Hebrew first name
                    </label>
                    <input
                      type="text"
                      dir="rtl"
                      value={formData.hebrewFirstName || ""}
                      onChange={(event) =>
                        setFormData((current) => ({
                          ...current,
                          hebrewFirstName: event.target.value,
                        }))
                      }
                      className={fieldClassName(false)}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Hebrew last name
                    </label>
                    <input
                      type="text"
                      dir="rtl"
                      value={formData.hebrewLastName || ""}
                      onChange={(event) =>
                        setFormData((current) => ({
                          ...current,
                          hebrewLastName: event.target.value,
                        }))
                      }
                      className={fieldClassName(false)}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Gender
                    </label>
                    <select
                      value={formData.gender || "Female"}
                      onChange={(event) =>
                        setFormData((current) => ({
                          ...current,
                          gender: event.target.value as FacultyMember["gender"],
                        }))
                      }
                      className={fieldClassName(false)}
                    >
                      <option value="Female">Female</option>
                      <option value="Male">Male</option>
                    </select>
                  </div>
                  <div className="col-span-1 md:col-span-2 mt-2">
                    <label className="mb-2 block text-sm font-medium text-iron-grey">
                      Profile photo
                    </label>
                    <div className="flex items-center gap-4">
                      {profilePreview ? (
                        <img
                          src={profilePreview}
                          alt="Faculty profile preview"
                          className="h-16 w-16 rounded-full border border-border object-cover"
                        />
                      ) : (
                        <div className="flex h-16 w-16 items-center justify-center rounded-full border border-border bg-muted text-text-secondary">
                          <Upload className="h-6 w-6" />
                        </div>
                      )}
                      <label className="cursor-pointer rounded-md border border-border px-3 py-1.5 text-sm font-medium text-iron-grey transition-colors hover:bg-muted">
                        Upload photo
                        <input
                          type="file"
                          accept="image/jpeg,image/png"
                          onChange={(event) =>
                            setFormData((current) => ({
                              ...current,
                              profilePhotoFile: event.target.files?.[0] ?? null,
                              profilePhotoRemoved: false,
                            }))
                          }
                          className="sr-only"
                        />
                      </label>
                      <span className="text-xs text-text-tertiary">
                        JPG, PNG up to 5MB
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <hr className="border-border" />

              <section>
                <h4 className="mb-4 font-title text-sm font-semibold uppercase tracking-wider text-iron-grey">
                  Contact information
                </h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Work email <span className="text-strawberry">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.workEmail || ""}
                      onChange={(event) =>
                        setFormData((current) => ({
                          ...current,
                          workEmail: event.target.value,
                        }))
                      }
                      className={fieldClassName(Boolean(errors.workEmail))}
                    />
                    <ErrorText message={errors.workEmail} />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Cell phone <span className="text-strawberry">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.cellPhone || ""}
                      onChange={(event) =>
                        setFormData((current) => ({
                          ...current,
                          cellPhone: event.target.value,
                        }))
                      }
                      className={fieldClassName(Boolean(errors.cellPhone))}
                    />
                    <ErrorText message={errors.cellPhone} />
                  </div>
                </div>
              </section>

              <hr className="border-border" />

              <section>
                <h4 className="mb-4 font-title text-sm font-semibold uppercase tracking-wider text-iron-grey">
                  Employment details
                </h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Employee ID
                    </label>
                    <input
                      type="text"
                      value={formData.employeeId || ""}
                      disabled
                      className="w-full cursor-not-allowed rounded-md border border-border bg-muted px-3 py-2 text-sm text-text-secondary"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Hire date <span className="text-strawberry">*</span>
                    </label>
                    <TailwindDateField
                      value={formData.hireDate || ""}
                      onChange={(event) =>
                        setFormData((current) => ({
                          ...current,
                          hireDate: event.target.value,
                        }))
                      }
                      className={fieldClassName(Boolean(errors.hireDate))}
                    />
                    <ErrorText message={errors.hireDate} />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Position
                    </label>
                    <select
                      value={formData.position || "Teacher"}
                      onChange={(event) =>
                        setFormData((current) => ({
                          ...current,
                          position: event.target
                            .value as FacultyMember["position"],
                        }))
                      }
                      className={fieldClassName(false)}
                    >
                      <option value="Teacher">Teacher</option>
                      <option value="Teacher's Aide">Teacher's Aide</option>
                      <option value="Specialist">Specialist</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Status
                    </label>
                    <select
                      value={formData.status || "active"}
                      onChange={(event) =>
                        setFormData((current) => ({
                          ...current,
                          status: event.target.value as FacultyMember["status"],
                        }))
                      }
                      className={fieldClassName(false)}
                    >
                      {Object.entries(FACULTY_STATUS_LABELS).map(
                        ([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                </div>
              </section>

              <hr className="border-border" />

              <section>
                <h4 className="mb-4 font-title text-sm font-semibold uppercase tracking-wider text-iron-grey">
                  Emergency contact
                </h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Contact name <span className="text-strawberry">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.emergencyContact?.name || ""}
                      onChange={(event) =>
                        handleEmergencyChange("name", event.target.value)
                      }
                      className={fieldClassName(Boolean(errors.emergencyName))}
                    />
                    <ErrorText message={errors.emergencyName} />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Relationship <span className="text-strawberry">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.emergencyContact?.relationship || ""}
                      onChange={(event) =>
                        handleEmergencyChange(
                          "relationship",
                          event.target.value,
                        )
                      }
                      className={fieldClassName(
                        Boolean(errors.emergencyRelationship),
                      )}
                    />
                    <ErrorText message={errors.emergencyRelationship} />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-iron-grey">
                      Phone number <span className="text-strawberry">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.emergencyContact?.phone || ""}
                      onChange={(event) =>
                        handleEmergencyChange("phone", event.target.value)
                      }
                      className={fieldClassName(Boolean(errors.emergencyPhone))}
                    />
                    <ErrorText message={errors.emergencyPhone} />
                  </div>
                </div>
              </section>
            </form>
          </div>
          <div className="border-t border-border bg-muted px-4 py-3 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-h-[2.5rem] items-center">
                {!isEditMode && facultyInviteLink ? (
                  <button
                    type="button"
                    data-testid="faculty-copy-invite-link"
                    onClick={() => {
                      void handleCopyInviteLink();
                    }}
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-2 text-sm font-medium text-primary transition-colors hover:bg-white/80 hover:text-primary-hover focus:outline-none sm:w-auto sm:justify-start"
                  >
                    <LinkIcon className="h-4 w-4" />
                    <span>
                      {inviteCopyStatus === "copied"
                        ? "Invite link copied"
                        : inviteCopyStatus === "failed"
                          ? "Copy failed"
                          : "Copy invite link"}
                    </span>
                  </button>
                ) : null}
              </div>
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  data-testid={isEditMode ? "cancel-edit" : "cancel-btn"}
                  className="w-full rounded-md border border-border bg-white px-4 py-2 text-base font-medium text-iron-grey shadow-sm transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:w-auto sm:text-sm"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid={isEditMode ? "save-changes" : "save-faculty-btn"}
                  className="w-full rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-sm transition-colors hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:w-auto sm:text-sm"
                  onClick={() => void handleSubmit()}
                >
                  Save faculty
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
